<template>
  <div>
    <header-component />
    <div class="px-2 py-2 w-full">
      <div class="card">
        <div class="flex justify-between items-center p-2">
          <div>
            <h3
              class="mb-0 w-full font-bold text-gray-800 text-md"
              style="text-transform: capitalize"
            >
              Branches
            </h3>
          </div>
          <div class="flex items-center">
            <base-button size="sm" type="primary" @click="openAdd">Add Branch</base-button>
          </div>
        </div>

        <!-- Loading spinner -->
        <div v-if="loading" class="flex justify-center py-12">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Name
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Address
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Phone
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Latitude
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Longitude
                </th>
                <th
                  class="px-2 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase border-b"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="branch in branches"
                :key="branch.id"
                class="hover:bg-gray-50"
              >
                <td class="px-2 py-2 text-sm text-gray-900 border-b">
                  {{ branch.name }}
                </td>
                <td class="px-2 py-2 text-sm text-gray-900 border-b">
                  {{ branch.address }}
                </td>
                <td class="px-2 py-2 text-sm text-gray-900 whitespace-nowrap border-b">
                  {{ branch.phone }}
                </td>
                <td class="px-2 py-2 text-sm text-gray-900 whitespace-nowrap border-b">
                  {{ branch.latitude != null ? branch.latitude : '—' }}
                </td>
                <td class="px-2 py-2 text-sm text-gray-900 whitespace-nowrap border-b">
                  {{ branch.longitude != null ? branch.longitude : '—' }}
                </td>
                <td class="px-2 py-2 text-sm text-gray-900 whitespace-nowrap border-b">
                  <div class="flex items-center gap-2">
                    <base-button size="sm" type="secondary" @click="openEdit(branch)">Edit</base-button>
                    <base-button size="sm" type="danger" @click="deleteBranch(branch)">Delete</base-button>
                  </div>
                </td>
              </tr>
              <tr v-if="!branches || branches.length === 0">
                <td
                  colspan="6"
                  class="px-2 py-4 text-sm text-center text-gray-500 border-b"
                >
                  No branches found.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add / Edit Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
    >
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold text-gray-800">
            {{ editing ? 'Edit Branch' : 'Add Branch' }}
          </h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">&#x2715;</button>
        </div>

        <!-- Name -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
          <input
            v-model="form.name"
            type="text"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="{ 'border-red-400': formErrors.name }"
            placeholder="Branch name"
          />
          <p v-if="formErrors.name" class="text-red-500 text-xs mt-1">{{ formErrors.name }}</p>
        </div>

        <!-- Address -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Address <span class="text-red-500">*</span></label>
          <textarea
            v-model="form.address"
            rows="2"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="{ 'border-red-400': formErrors.address }"
            placeholder="Branch address"
          ></textarea>
          <p v-if="formErrors.address" class="text-red-500 text-xs mt-1">{{ formErrors.address }}</p>
        </div>

        <!-- Phone -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Phone <span class="text-red-500">*</span></label>
          <input
            v-model="form.phone"
            type="text"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="{ 'border-red-400': formErrors.phone }"
            placeholder="Phone number"
          />
          <p v-if="formErrors.phone" class="text-red-500 text-xs mt-1">{{ formErrors.phone }}</p>
        </div>

        <!-- Latitude -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
          <input
            v-model="form.latitude"
            type="number"
            step="0.000001"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="{ 'border-red-400': formErrors.latitude }"
            placeholder="e.g. 18.520430"
          />
          <p v-if="formErrors.latitude" class="text-red-500 text-xs mt-1">{{ formErrors.latitude }}</p>
          <p class="text-gray-400 text-xs mt-1">Used for geofencing attendance within 100m radius</p>
        </div>

        <!-- Longitude -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
          <input
            v-model="form.longitude"
            type="number"
            step="0.000001"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            :class="{ 'border-red-400': formErrors.longitude }"
            placeholder="e.g. 73.856743"
          />
          <p v-if="formErrors.longitude" class="text-red-500 text-xs mt-1">{{ formErrors.longitude }}</p>
          <p class="text-gray-400 text-xs mt-1">Used for geofencing attendance within 100m radius</p>
        </div>

        <div class="flex gap-3 mt-6">
          <base-button type="secondary" @click="closeModal" class="flex-1">Cancel</base-button>
          <base-button type="primary" @click="save" :disabled="saving" class="flex-1">
            <span v-if="saving">
              <span class="inline-block animate-spin mr-1">&#8987;</span> Saving...
            </span>
            <span v-else>Save</span>
          </base-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";
import headerComponent from "../Components/HeaderComponent";

export default {
  name: "BranchList",
  components: {
    "header-component": headerComponent
  },
  data() {
    return {
      branches: [],
      loading: false,
      showModal: false,
      editing: null,
      form: {
        name: "",
        address: "",
        phone: "",
        latitude: "",
        longitude: ""
      },
      formErrors: {},
      saving: false
    };
  },
  created() {
    this.fetchBranches();
  },
  methods: {
    async fetchBranches() {
      this.loading = true;
      try {
        const res = await new Api().call(
          "get",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`
        );
        this.branches = (res && res.data && res.data.data) || [];
      } catch (err) {
        window.notify &&
          window.notify({
            message: "Failed to load branches.",
            type: "danger",
            title: "Error"
          });
      } finally {
        this.loading = false;
      }
    },
    openAdd() {
      this.editing = null;
      this.form = { name: "", address: "", phone: "", latitude: "", longitude: "" };
      this.formErrors = {};
      this.showModal = true;
    },
    openEdit(branch) {
      this.editing = branch;
      this.form = {
        name: branch.name || "",
        address: branch.address || "",
        phone: branch.phone || "",
        latitude: branch.latitude != null ? branch.latitude : "",
        longitude: branch.longitude != null ? branch.longitude : ""
      };
      this.formErrors = {};
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editing = null;
      this.form = { name: "", address: "", phone: "", latitude: "", longitude: "" };
      this.formErrors = {};
    },
    validate() {
      const errors = {};
      if (!this.form.name || !this.form.name.trim()) {
        errors.name = "Name is required.";
      }
      if (!this.form.address || !this.form.address.trim()) {
        errors.address = "Address is required.";
      }
      if (!this.form.phone || !this.form.phone.trim()) {
        errors.phone = "Phone is required.";
      }
      this.formErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async save() {
      if (!this.validate()) return;

      this.saving = true;
      const payload = {
        name: this.form.name.trim(),
        address: this.form.address.trim(),
        phone: this.form.phone.trim()
      };
      if (this.form.latitude !== "" && this.form.latitude !== null) {
        payload.latitude = this.form.latitude;
      }
      if (this.form.longitude !== "" && this.form.longitude !== null) {
        payload.longitude = this.form.longitude;
      }

      try {
        if (this.editing) {
          await new Api().call(
            "patch",
            `${endpoints.LARAVEL_BASE_URL}/api/v1/branches/${this.editing.id}`,
            payload
          );
          window.notify &&
            window.notify({
              message: "Branch updated successfully.",
              type: "success",
              title: "Success"
            });
        } else {
          await new Api().call(
            "post",
            `${endpoints.LARAVEL_BASE_URL}/api/v1/branches`,
            payload
          );
          window.notify &&
            window.notify({
              message: "Branch created successfully.",
              type: "success",
              title: "Success"
            });
        }
        this.closeModal();
        await this.fetchBranches();
      } catch (err) {
        window.notify &&
          window.notify({
            message: "Failed to save branch. Please try again.",
            type: "danger",
            title: "Error"
          });
      } finally {
        this.saving = false;
      }
    },
    async deleteBranch(branch) {
      const confirmed = window.confirm(`Delete branch "${branch.name}"?`);
      if (!confirmed) return;

      try {
        await new Api().call(
          "delete",
          `${endpoints.LARAVEL_BASE_URL}/api/v1/branches/${branch.id}`
        );
        window.notify &&
          window.notify({
            message: "Branch deleted successfully.",
            type: "success",
            title: "Success"
          });
        await this.fetchBranches();
      } catch (err) {
        window.notify &&
          window.notify({
            message: "Failed to delete branch. Please try again.",
            type: "danger",
            title: "Error"
          });
      }
    }
  }
};
</script>
